import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { Spinner } from '@/ui';
import { DatePresetBar } from '@/components';
import { useAppDispatch, useAppState } from '@/store';
import { Resource, usePermissions } from '@/modules/Permissions';
import { DateFormats } from '@/constants/dates';
import { CancelledOrdersGroupBy, DatePresetKey } from '@/models/cancelledOrders.model';
import {
  AllPartnersModal,
  AllReasonsModal,
  ChartsBlock,
  ExportPdfButton,
  KpiList,
  PartnersTable,
  ReasonsTable,
} from '../../components';
import {
  cancelledOrdersSelector,
  getCancelledOrdersData,
  getCancelledPartnersList,
  setCancelledOrdersGroupBy,
  setCancelledOrdersPreset,
  setCancelledOrdersQuery,
} from '../../feature';
import { resolveDatePreset, resolveGroupByForRange, toCancelledOrdersQuery } from '../../helpers';
import {
  StyledBottomGrid,
  StyledFiltersRow,
  StyledHeaderRow,
  StyledKpiCell,
  StyledNoPrint,
  StyledPageSubtitle,
  StyledPageTitle,
  StyledPageWrapper,
  StyledPartnersCell,
  StyledPeriodLabel,
  StyledReasonsCell,
  StyledReportRoot,
  StyledTablesStack,
  StyledTitleBlock,
} from './CancelledOrdersMainContainerStyles';

const CancelledOrdersMainContainer = () => {
  const { t } = useTranslation(['dashboard']);
  const dispatch = useAppDispatch();
  const { canView } = usePermissions();
  const { data, isLoading, isListLoading, partnersList, preset, groupBy, query } = useAppState(cancelledOrdersSelector);
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(null);
  const [customTo, setCustomTo] = useState<Dayjs | null>(null);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const groupByTouchedRef = useRef(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const canViewDashboard = canView(Resource.DASHBOARD);

  const range = useMemo(() => resolveDatePreset(preset, customFrom, customTo), [preset, customFrom, customTo]);

  useEffect(() => {
    if (groupByTouchedRef.current) return;
    const nextGroupBy = resolveGroupByForRange(preset, range.dateFrom, range.dateTo);
    if (nextGroupBy !== groupBy) {
      dispatch(setCancelledOrdersGroupBy(nextGroupBy));
    }
  }, [preset, range.dateFrom, range.dateTo, groupBy, dispatch]);

  useEffect(() => {
    if (!canViewDashboard) return;
    if (preset === 'custom' && (!customFrom || !customTo)) return;

    const nextQuery = toCancelledOrdersQuery(range, groupBy);
    dispatch(setCancelledOrdersQuery(nextQuery));
    dispatch(getCancelledOrdersData(nextQuery));
    dispatch(
      getCancelledPartnersList({
        date_from: nextQuery.date_from,
        date_to: nextQuery.date_to,
        page: 1,
        per_page: 100,
      }),
    );
  }, [canViewDashboard, range, groupBy, preset, customFrom, customTo, dispatch]);

  const handlePresetChange = (nextPreset: DatePresetKey) => {
    groupByTouchedRef.current = false;
    dispatch(setCancelledOrdersPreset(nextPreset));
    if (nextPreset !== 'custom') {
      setCustomFrom(null);
      setCustomTo(null);
    }
  };

  const handleCustomRangeChange = (from: Dayjs | null, to: Dayjs | null) => {
    groupByTouchedRef.current = false;
    setCustomFrom(from);
    setCustomTo(to);
    dispatch(setCancelledOrdersPreset('custom'));
  };

  const handleGroupByChange = (value: CancelledOrdersGroupBy) => {
    groupByTouchedRef.current = true;
    dispatch(setCancelledOrdersGroupBy(value));
  };

  const periodLabel =
    query.date_from && query.date_to
      ? `${dayjs(query.date_from).format(DateFormats.EUROPEAN_DEFAULT)} — ${dayjs(query.date_to).format(
          DateFormats.EUROPEAN_DEFAULT,
        )}`
      : t('cancelled_orders_preset_all_time');

  return (
    <StyledPageWrapper>
      <StyledReportRoot ref={reportRef} id="cancelled-orders-report">
        <StyledHeaderRow>
          <StyledTitleBlock>
            <StyledPageTitle>{t('cancelled_orders_title')}</StyledPageTitle>
            <StyledNoPrint>
              <StyledPageSubtitle>{t('cancelled_orders_subtitle')}</StyledPageSubtitle>
            </StyledNoPrint>
            <StyledPeriodLabel>
              {t('cancelled_orders_period')}: {periodLabel}
            </StyledPeriodLabel>
          </StyledTitleBlock>
          <StyledNoPrint>
            <ExportPdfButton
              disabled={isLoading && !data}
              targetRef={reportRef}
              fileName={`cancelled-orders_${query.date_from ?? 'all'}_${query.date_to ?? 'all'}.pdf`}
              onExportStateChange={setPdfExporting}
            />
          </StyledNoPrint>
        </StyledHeaderRow>

        <StyledNoPrint>
          <StyledFiltersRow>
            <DatePresetBar
              preset={preset}
              dateFrom={range.dateFrom}
              dateTo={range.dateTo}
              onPresetChange={handlePresetChange}
              onCustomRangeChange={handleCustomRangeChange}
            />
          </StyledFiltersRow>
        </StyledNoPrint>

        {isLoading && !data ? (
          <Spinner />
        ) : (
          <>
            <ChartsBlock
              data={data}
              groupBy={groupBy}
              onGroupByChange={handleGroupByChange}
              pdfExporting={pdfExporting}
            />
            <StyledBottomGrid>
              <StyledKpiCell>
                <KpiList kpis={data?.kpis} />
              </StyledKpiCell>
              <StyledTablesStack>
                <StyledReasonsCell>
                  <ReasonsTable
                    items={data?.reasons ?? []}
                    loading={isLoading}
                    onViewAll={() => setReasonsOpen(true)}
                  />
                </StyledReasonsCell>
                <StyledPartnersCell>
                  <PartnersTable
                    items={partnersList}
                    loading={isLoading || isListLoading}
                    onViewAll={() => setPartnersOpen(true)}
                  />
                </StyledPartnersCell>
              </StyledTablesStack>
            </StyledBottomGrid>
          </>
        )}
      </StyledReportRoot>

      <AllReasonsModal open={reasonsOpen} onClose={() => setReasonsOpen(false)} />
      <AllPartnersModal open={partnersOpen} onClose={() => setPartnersOpen(false)} />
    </StyledPageWrapper>
  );
};

export default CancelledOrdersMainContainer;
